import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { BookOpenText, ExternalLink, Globe, MessageSquareQuote } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { NoteContent } from '@/components/NoteContent';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthor } from '@/hooks/useAuthor';
import { getDisplayName } from '@/lib/getDisplayName';
import type { HighlightSource } from '@/lib/highlights';
import { normalizeUrl } from '@/lib/normalizeUrl';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

function articleMeta(event: NostrEvent) {
  const tag = (name: string) => event.tags.find(([n]) => n === name)?.[1];
  return {
    title: tag('title')?.trim() || 'Untitled article',
    image: sanitizeUrl(tag('image')),
    publishedAt: Number(tag('published_at')) || event.created_at,
  };
}

function ArticleSourceCard({ source, event }: { source: Extract<HighlightSource, { type: 'article' }>; event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const meta = articleMeta(event);
  const naddr = nip19.naddrEncode({
    kind: source.kind,
    pubkey: source.pubkey,
    identifier: source.identifier,
  });

  return (
    <Link
      to={`/${naddr}`}
      className="group flex gap-3 rounded-lg border border-border/80 bg-background/60 p-3 transition-colors hover:border-primary/50 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {meta.image ? (
        <img
          src={meta.image}
          alt=""
          loading="lazy"
          className="hidden sm:block size-16 shrink-0 rounded-md object-cover border border-border/60"
        />
      ) : (
        <span className="hidden sm:flex size-16 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground border border-border/60">
          <BookOpenText className="size-6" />
        </span>
      )}
      <span className="min-w-0 flex flex-col justify-center gap-0.5">
        <span className="font-medium leading-snug line-clamp-2 group-hover:underline underline-offset-4 decoration-primary/60">
          {meta.title}
        </span>
        <span className="text-xs text-muted-foreground truncate">
          by {getDisplayName(author.data?.metadata, event.pubkey)}
        </span>
      </span>
    </Link>
  );
}

function NoteSourceCard({ event }: { event: NostrEvent }) {
  const author = useAuthor(event.pubkey);
  const note = nip19.noteEncode(event.id);

  return (
    <div className="group relative flex gap-3 rounded-lg border border-border/80 bg-background/60 p-3 transition-colors hover:border-primary/50 hover:bg-accent/40">
      {/* Stretched link keeps nested anchors in the note content valid. */}
      <Link
        to={`/${note}`}
        aria-label="View note"
        className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <span className="hidden sm:flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground border border-border/60">
        <MessageSquareQuote className="size-5" />
      </span>
      <span className="min-w-0">
        <span className="block text-xs text-muted-foreground mb-0.5">
          Note by {getDisplayName(author.data?.metadata, event.pubkey)}
        </span>
        <NoteContent
          event={event}
          className="text-sm line-clamp-3 [&_a]:pointer-events-none"
          disableEmbeds
        />
      </span>
    </div>
  );
}

function UrlSourceCard({ url }: { url: string }) {
  const href = sanitizeUrl(url);
  if (!href) return null;

  const parsed = new URL(href);
  const displayPath = parsed.pathname === '/' ? '' : parsed.pathname;

  return (
    <div className="group relative flex items-center gap-3 rounded-lg border border-border/80 bg-background/60 p-3 transition-colors hover:border-primary/50 hover:bg-accent/40">
      {/* Stretched internal link to the page's highlights. */}
      <Link
        to={`/web/${encodeURIComponent(normalizeUrl(href) ?? href)}`}
        aria-label={`Highlights of ${parsed.hostname}`}
        className="absolute inset-0 z-10 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground border border-border/60">
        <Globe className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium truncate">{parsed.hostname}</span>
        {displayPath && (
          <span className="block text-xs text-muted-foreground truncate">{displayPath}</span>
        )}
      </span>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open original page"
        className="relative z-20 shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:text-foreground hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ExternalLink className="size-4" />
      </a>
    </div>
  );
}

interface HighlightSourceCardProps {
  source: HighlightSource;
  /** The resolved Nostr event for article/note sources, if loaded. */
  event?: NostrEvent;
  /** True while source resolution is in flight. */
  loading?: boolean;
}

/** Card linking to whatever the highlight was taken from. */
export function HighlightSourceCard({ source, event, loading }: HighlightSourceCardProps) {
  if (source.type === 'url') {
    return <UrlSourceCard url={source.url} />;
  }

  if (!event) {
    if (loading) {
      return <Skeleton className="h-[4.5rem] w-full rounded-lg" />;
    }
    return null;
  }

  if (source.type === 'article') {
    return <ArticleSourceCard source={source} event={event} />;
  }

  return <NoteSourceCard event={event} />;
}
